import { Box, Container, Text, Heading, VStack } from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { useEffect } from "react";

const TermsAndPrivacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box py={8}>
      <Helmet>
        <title>Terms of Use and Privacy Policy | BulletScout</title>
        <meta
          name="description"
          content="Terms of Use and Privacy Policy for BulletScout.com"
        />
      </Helmet>

      <Container maxW="4xl" pt={"50px"}>
        <VStack spacing={8} align="stretch">
          <Heading as="h1" size="xl" textAlign="center">
            BulletScout.com Terms of Use & Privacy Policy
          </Heading>
          <Text textAlign="center" color="gray.500">
            Last updated: June 6, 2025
          </Text>

          <Box>
            <Heading as="h2" size="lg" mb={4}>
              Use of the BulletScout.com Site and Services
            </Heading>
            <Text mb={4}>
              Welcome to www.BulletScout.com ("BulletScout.com" or the "Site").
              By using BulletScout.com you agree to the following:
            </Text>
            <VStack align="stretch" spacing={2} pl={4}>
              <Text>
                • You agree to comply with and be bound by these Terms of Use
                and our Privacy Policy;
              </Text>
              <Text>
                • You warrant that you are at least 21 years of age or older;
              </Text>
              <Text>
                • You agree to use the Site only for lawful purposes; and
              </Text>
              <Text>
                • You agree not to misuse or interfere with the operation of the
                Site.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Heading as="h2" size="lg" mb={4}>
              About the BulletScout.com Site
            </Heading>
            <Text mb={4}>
              BulletScout.com is owned and operated by Quick Take Retail, LLC
              ("BulletScout"), and you can contact us through our "Contact Us"
              page, or via our registered agent as follows: Registered Agents,
              Inc., 530-B Harkle Road, Ste. 100, Santa Fe, NM 87505.
            </Text>
            <Text mb={4}>
              BulletScout.com is an informational and comparison shopping tool
              that helps users find the best prices of ammunition by collecting,
              standardizing, organizing, and displaying product listings from
              third-party websites which sell ammunition and related products
              ("Retailers").
            </Text>
            <Text mb={4}>
              Information on BulletsScout.com is for informational purposes
              only, and you agree we are not liable for any damages resulting
              from reliance on information on our Site.
            </Text>
            <Text>
              BulletScout does not directly sell ammunition or related products.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="lg" mb={4}>
              Third Party Links & Websites
            </Heading>
            <Text mb={4}>
              BulletScout.com references, and contains links to, the third-party
              websites of Retailers. BulletScout does not vet or endorse the
              third-party websites or Retailers referenced on or linked to by
              the Site.
            </Text>
            <Text mb={4}>
              BulletScout is not responsible for the content, policies, or
              practices of these third-party websites and Retailers. You agree
              that BulletScout is not liable for any damages resulting from
              reliance on, purchase of, or use of information, products or
              services from these third-party websites and Retailers.
            </Text>
            <Text mb={4}>
              All transactions or communications you may have on or with these
              third-party websites and Retailers are solely between you and
              them. If you make a purchase from a third-party website or
              Retailer, please contact them directly for any problems or issues
              you may encounter during the purchase process or with items or
              services you have purchased. BulletScout is unable to contact
              these third-party websites or Retailers on your behalf.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="lg" mb={4}>
              Accuracy and Availability
            </Heading>
            <Text mb={4}>
              We do our best to keep the information on BulletScout.com as
              accurate and as up-to-date as possible (including item
              specification, item prices, shipping costs, and availability).
              However, we can't guarantee the accuracy of this information or
              the price or availability of products or shipping on the
              third-party websites or Retailers.
            </Text>
            <Text mb={4}>
              The listings, prices, and availability of items displayed on
              BulletScout.com, and on the third-party websites of Retailers may
              change without notice. You understand we are not responsible for
              out-of-date listings or inaccuracies on BulletScout.com or on the
              third-party websites of retailers (including inaccuracies related
              to item costs, item availability, shipping costs, or item
              specifications).
            </Text>
            <Text mb={4}>
              You are solely responsible for how you use the information or
              materials on BulletScout.com. You agree we are not liable for any
              damages resulting from your use of the Site or the information
              contained therein. This includes, but is not limited to, any
              information as to the suitability of a particular type of
              ammunition for a particular purpose or use in a particular
              firearm. It is up to you to make sure that any products, services,
              or information you rely on meet your personal needs.
            </Text>
            <Text>
              If you see something on the BulletScout.com website that you think
              is inappropriate, incorrect, or otherwise needs to be modified,
              clarified, or removed, please notify us by using our "Contact Us"
              page. If you find individual listings or Retailer offers listed on
              the BulletScout.com site which are out of stock, or have an error
              related to price, quantity or product information, please utilize
              the flag in the bottom right-hand corner of the listing or offer
              to report the error.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="lg" mb={4}>
              Shipping & Product Prices Disclaimer
            </Heading>
            <Text mb={4}>
              One of the unique features of the BulletScout.com website is the
              inclusion of both product and shipping costs charged by Retailers
              into the total item price and cost per round calculations that are
              displayed on the Site. BulletScout codes each unique product offer
              with a green, yellow, or red badge to help you better understand
              how the estimated shipping cost affects the displayed total cost
              and cost per round. The color coded badges correspond to the
              following:
            </Text>
            <VStack align="stretch" spacing={2} pl={4}>
              <Text>
                • <strong>Green Badge (Verified):</strong> BulletScout has
                verified the shipping cost associated with this offer is
                accurate as of our most recent check. The price you see for
                product and shipping costs should be the price you pay when
                purchasing on the Retailer's site.
              </Text>
              <Text>
                • <strong>Yellow Badge (Caution):</strong> BulletScout has
                estimated the shipping cost associated with this offer, but it
                may not be fully accurate. The price you see for product and
                shipping costs should be very close to the price you pay when
                purchasing on the Retailer's site.
              </Text>
              <Text>
                • <strong>Red Badge (Unknown):</strong> BulletScout was unable
                to determine the shipping cost associated with this offer with
                any degree of confidence. We have provided a default shipping
                cost for these offers of $30.00, which may or may not be more or
                less than the price you pay for shipping when purchasing at the
                Retailer's site.
              </Text>
            </VStack>
            <Text mt={4}>
              By default, we exclude offers with red badges from the search
              results we display when you search for a product on the
              BulletScout.com website. We do, however, offer you the option to
              include these offers back into the search results.
            </Text>
            <Text mt={4}>
              We make every effort to monitor for changes in product and
              shipping prices, and to update our Site accordingly. However,
              always be sure to check the total price you are being charged
              before completing a purchase on any Retailer's site.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="lg" mb={4}>
              Affiliate Disclaimer
            </Heading>
            <Text>
              Some of the third-party or Retailer websites referenced by or
              linked-to from BulletScout.com may also be affiliate websites, and
              we may receive a commission or other payment if you visit,
              purchase from, or use products on these websites.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="lg" mb={4}>
              Intellectual Property
            </Heading>
            <Text mb={4}>
              BulletScout.com contains information and material (including but
              not limited to text, graphics, images, data, and the design,
              layout, and look of information and graphics) which may be the
              sole property of, or licensed by, BulletScout. You may not
              collect, reproduce, distribute, or otherwise use this information
              or material without our prior consent.
            </Text>
            <Text mb={4}>
              The aggregated content of BulletScout.com is the sole property of
              BulletScout and may not be collected, reproduced, redistributed,
              or otherwise used without our prior consent.
            </Text>
            <Text mb={4}>
              Scraping or otherwise compiling or collecting the information
              provided on BulletScout.com in an automated manner (including via
              use of services such as ReloadEvery and Refresh Money) is strictly
              prohibited.
            </Text>
            <Text>
              If you find content on our Site which you believe is being used
              without your permission or which may be infringing on your
              intellectual property, please notify us immediately by utilizing
              our "Contact Us" page.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="lg" mb={4}>
              Disclaimer Regarding Ratings, Reviews, & Artificial Intelligence
            </Heading>
            <Text mb={4}>
              BulletScout may use Artificial Intelligence ("AI"), including but
              not limited to Large Language Models, to collect, standardize,
              organize, and display content on the BulletScout.com website. We
              may also use AI to curate search results and suggest product
              purchases. BulletScout makes no claim as to the accuracy of AI
              generated search results or as to the suitability of a suggested
              product for any particular purpose.
            </Text>
            <Text>
              The ratings, reviews, and information associated with the products
              or Retailers displayed on the Site may be an AI generated summary
              or aggregation of information collected from other sources.
              BulletScout makes no claim as to the accuracy of these AI
              generated summaries, aggregations, or the information used to
              create them.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="lg" mb={4}>
              Privacy Policy
            </Heading>
            <Text mb={4}>
              By using the Site, you agree you are accepting the practices
              described below in our Privacy Policy. The policies and practices
              described below apply only to BulletScout and do not apply to any
              third-party websites or Retrailers which may or may not have their
              own specific privacy policies and practices.
            </Text>

            <Heading as="h3" size="md" mb={4}>
              Information We May Collect:
            </Heading>
            <VStack align="stretch" spacing={2} pl={4}>
              <Text>
                • When you use the site, or respond to or interact with an
                email, survey or other communication, we may collect basic
                information like your zip code, approximate geolocation, IP
                address, IP geolocation, browser type, and browsing behavior.
              </Text>
              <Text>
                • If you sign up for our newsletter, our email list, a contest,
                or otherwise contact us, we may collect your email address and
                other contact information.
              </Text>
              <Text>
                • When voluntarily submitted by users of the Site, we may
                collect personally identifiable information such as names, phone
                numbers, postal addresses, and email addresses.
              </Text>
            </VStack>

            <Heading as="h3" size="md" mt={6} mb={4}>
              How We Use Your Information:
            </Heading>
            <VStack align="stretch" spacing={2} pl={4}>
              <Text>
                • We may use your information to operate and improve
                BulletScout.com, and to administer contests, promotions, surveys
                or other site features;
              </Text>
              <Text>
                • We may use your information to personalize your experience on
                the Site and any communications you are sent, including
                delivering the content and product offerings in which you are
                most interested.
              </Text>
              <Text>
                • We may use your provided zip code or other geolocation to
                customize which results are displayed to you and/or to show you
                more accurate shipping and cost information;
              </Text>
              <Text>
                • To communicate with you, including responding to any requests
                you may submit; and
              </Text>
              <Text>• To analyze Site traffic and trends.</Text>
            </VStack>

            <Heading as="h3" size="md" mt={6} mb={4}>
              Sharing Your Information:
            </Heading>
            <VStack align="stretch" spacing={2} pl={4}>
              <Text>
                • We may share anonymous usage data with analytics services,
                Retailers, or other users of our site.
              </Text>
              <Text>
                • We may share your email address with our partners or
                subsidiaries.
              </Text>
              <Text>
                • We may share any information we collect (a) as required by law
                or court order; and (b) with law enforcement or other necessary
                parties in order to prevent fraud from occurring or in order to
                investigate fraud that may have already occurred.
              </Text>
            </VStack>

            <Heading as="h3" size="md" mt={6} mb={4}>
              Cookies:
            </Heading>
            <Text mb={4}>
              We use cookies to enhance your experience and understand usage.
            </Text>
            <Text>You can control cookies through your browser settings.</Text>

            <Heading as="h3" size="md" mt={6} mb={4}>
              Opting Out:
            </Heading>
            <Text>
              You may opt out of receiving any further information from
              BulletScout by clicking the "Unsubscribe" link at the bottom of
              any email you have been sent.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="lg" mb={4}>
              Changes to Terms of Use & Privacy Policy
            </Heading>
            <Text>
              We may update these Terms of Use and our Privacy Policy at any
              time. Changes will be posted here with an updated date.
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default TermsAndPrivacy;
